/**
 * API Client for Appeal Your Home Frontend
 * Handles all HTTP requests to the backend API
 */

import axios from 'axios';
import { API_CONFIG } from './config.js';

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = new Set([
  '/auth/register',
  '/auth/login',
  '/auth/refresh',
  '/auth/validate-setup-token',
  '/auth/complete-account-setup',
  '/auth/google/status',
  '/properties/validate-address-public',
  '/properties/validate-address-partial-public',
  '/properties/validate-with-parcel',
  '/properties/counties',
  '/properties/parcels/search',
  '/integration/payment/guest-checkout-session',
  '/public/property-analysis',
  '/public/health',
  '/flaws/definitions',
  // Parcel endpoints (Texas parcel database)
  '/parcels/lookup',
  '/parcels/search',
  '/parcels/counties'
]);

// Special handling for dynamic endpoints with parameters
const isPublicDynamicEndpoint = (url) => {
  // Counties search endpoint
  if (url.startsWith('/properties/counties/search')) {
    return true;
  }
  // Public property analysis status endpoint
  if (url.startsWith('/public/property-analysis/')) {
    return true;
  }
  // Parcel lookup and search (public for landing page)
  if (url.startsWith('/parcels/lookup') || url.startsWith('/parcels/search')) {
    return true;
  }
  // Properties parcel search endpoint
  if (url.startsWith('/properties/parcels/search')) {
    return true;
  }
  return false;
};

// Helper function to check if an endpoint is public
const isPublicEndpoint = (url) => {
  // Remove query parameters and normalize URL
  const cleanUrl = url.split('?')[0];
  return PUBLIC_ENDPOINTS.has(cleanUrl) || isPublicDynamicEndpoint(cleanUrl);
};

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
});

// Track if we're currently refreshing the token to prevent multiple refresh attempts
let isRefreshing = false;
// Queue of requests waiting for token refresh
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Attempt to refresh the access token using the refresh token
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('tbyt_refresh_token');
  
  if (!refreshToken) {
    return null;
  }

  try {
    // Use a separate axios instance to avoid interceptors
    const response = await axios.post(
      `${API_CONFIG.baseURL}/auth/refresh`,
      { refreshToken },
      { headers: API_CONFIG.headers }
    );

    if (response.data?.success && response.data?.data?.tokens) {
      const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
      
      // Store new tokens
      localStorage.setItem('tbyt_token', accessToken);
      if (newRefreshToken) {
        localStorage.setItem('tbyt_refresh_token', newRefreshToken);
      }
      
      if (import.meta.env.DEV) {
        console.log('🔄 Token refreshed successfully');
      }
      
      return accessToken;
    }
    
    return null;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.log('❌ Token refresh failed:', error.response?.data?.error?.message || error.message);
    }
    return null;
  }
};

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    // Only add auth token for non-public endpoints
    if (!isPublicEndpoint(config.url)) {
      const token = localStorage.getItem('tbyt_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Mark request as public for later reference in response interceptor
    config.isPublicEndpoint = isPublicEndpoint(config.url);
    
    // Log requests in development
    if (import.meta.env.DEV) {
      const authStatus = config.isPublicEndpoint ? '[PUBLIC]' : '[PROTECTED]';
      console.log(`🚀 API Request ${authStatus}: ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Helper to create an error that preserves response data
const createApiError = (message, originalError) => {
  const apiError = new Error(message);
  if (originalError?.response) {
    apiError.response = originalError.response;
  }
  return apiError;
};

// Response interceptor to handle common responses
axiosInstance.interceptors.response.use(
  (response) => {
    // Log responses in development
    if (import.meta.env.DEV) {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle common error cases
    if (error.response) {
      const { status, data } = error.response;
      const isPublic = originalRequest?.isPublicEndpoint;
      
      // Handle authentication errors
      if (status === 401) {
        // For public endpoints, just reject without refresh attempt
        if (isPublic) {
          console.log('❌ Public endpoint returned 401:', originalRequest.url);
          return Promise.reject(createApiError(data?.error?.message || 'Authentication failed', error));
        }
        
        // Don't attempt refresh for the refresh endpoint itself
        if (originalRequest.url === '/auth/refresh') {
          return Promise.reject(createApiError('Session expired. Please log in again.', error));
        }
        
        // Prevent infinite retry loops
        if (originalRequest._retry) {
          // Refresh already attempted and failed - clear tokens and redirect
          localStorage.removeItem('tbyt_token');
          localStorage.removeItem('tbyt_refresh_token');
          window.location.href = '/auth';
          return Promise.reject(createApiError('Session expired. Please log in again.', error));
        }
        
        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }
        
        // Mark request as retried and start refresh
        originalRequest._retry = true;
        isRefreshing = true;
        
        try {
          const newToken = await refreshAccessToken();
          
          if (newToken) {
            // Token refreshed successfully - retry original request
            processQueue(null, newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          } else {
            // Refresh failed - no refresh token or server rejected it
            processQueue(new Error('Token refresh failed'), null);
            localStorage.removeItem('tbyt_token');
            localStorage.removeItem('tbyt_refresh_token');
            window.location.href = '/auth';
            return Promise.reject(createApiError('Session expired. Please log in again.', error));
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.removeItem('tbyt_token');
          localStorage.removeItem('tbyt_refresh_token');
          window.location.href = '/auth';
          return Promise.reject(createApiError('Session expired. Please log in again.', error));
        } finally {
          isRefreshing = false;
        }
      }
      
      // Handle rate limiting
      if (status === 429) {
        return Promise.reject(createApiError('Too many requests. Please try again later.', error));
      }
      
      // Return API error message if available, preserving response for status checks
      if (data?.error?.message) {
        return Promise.reject(createApiError(data.error.message, error));
      }
    }
    
    // Handle network errors
    if (error.request && !error.response) {
      return Promise.reject(new Error('Network error. Please check your connection.'));
    }
    
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// API methods
export const apiClient = {
  // Generic methods
  get: (url, config) => axiosInstance.get(url, config),
  post: (url, data, config) => axiosInstance.post(url, data, config),
  put: (url, data, config) => axiosInstance.put(url, data, config),
  delete: (url, config) => axiosInstance.delete(url, config),
  patch: (url, data, config) => axiosInstance.patch(url, data, config),
  
  // Auth methods
  auth: {
    register: (userData) => axiosInstance.post('/auth/register', userData),
    login: (credentials) => axiosInstance.post('/auth/login', credentials),
    refresh: (data) => axiosInstance.post('/auth/refresh', data),
    logout: () => axiosInstance.post('/auth/logout'),
    resetPassword: (email) => axiosInstance.post('/auth/reset-password', { email }),
    verifyEmail: (token) => axiosInstance.get(`/auth/verify-email/${token}`),
    validateSetupToken: (token) => axiosInstance.post('/auth/validate-setup-token', { token }),
    completeAccountSetup: (data) => axiosInstance.post('/auth/complete-account-setup', data),
    // Google OAuth
    getGoogleOAuthStatus: () => axiosInstance.get('/auth/google/status'),
    // Note: Google OAuth initiate is a redirect, not an API call
    // Use: window.location.href = `${API_CONFIG.baseURL}/auth/google`
  },
  
  // User methods
  users: {
    getProfile: () => axiosInstance.get('/users/profile'),
    updateProfile: (data) => axiosInstance.put('/users/profile', data),
    addProperty: (propertyData) => axiosInstance.post('/users/properties', propertyData),
    updateProperty: (propertyId, data) => axiosInstance.put(`/users/properties/${propertyId}`, data),
    getProgress: () => axiosInstance.get('/users/progress'),
  },
  
  // Course methods
  courses: {
    getModules: (params) => axiosInstance.get('/courses/modules', { params }),
    getModule: (id, params) => axiosInstance.get(`/courses/modules/${id}`, { params }),
    updateProgress: (id, progressData) => axiosInstance.post(`/courses/modules/${id}/progress`, progressData),
    completeModule: (id) => axiosInstance.post(`/courses/modules/${id}/complete`),
    getResources: (params) => axiosInstance.get('/courses/resources', { params }),
  },
  
  // Property methods
  properties: {
    // Full address validation (requires county selection)
    validateAddress: (addressData) => axiosInstance.post('/properties/validate-address', addressData),
    validateAddressPublic: (addressData) => axiosInstance.post('/properties/validate-address-public', addressData),
    
    // Partial address validation (without county selection)
    validateAddressPartial: (addressData) => axiosInstance.post('/properties/validate-address-partial', addressData),
    validateAddressPartialPublic: (addressData) => axiosInstance.post('/properties/validate-address-partial-public', addressData),
    
    // Address validation with Texas parcel database lookup (public)
    // Returns parcel data including market value - used for landing page quote
    validateWithParcel: (addressData) => axiosInstance.post('/properties/validate-with-parcel', addressData),
    
    // Property classification
    classify: (propertyData) => axiosInstance.post('/properties/classify', propertyData),
    
    // County management
    getCounties: () => axiosInstance.get('/properties/counties'),
    getCounty: (id) => axiosInstance.get(`/properties/counties/${id}`),
    searchCounties: (searchTerm) => axiosInstance.get(`/properties/counties?search=${encodeURIComponent(searchTerm)}`),
    
    // Parcel search for address autocomplete (public)
    searchParcels: (query, city, county, limit = 10) => {
      const params = new URLSearchParams({ q: query });
      if (city) params.append('city', city);
      if (county) params.append('county', county);
      if (limit) params.append('limit', String(limit));
      return axiosInstance.get(`/properties/parcels/search?${params.toString()}`);
    },
  },
  
  // Texas Parcel Database methods (direct access)
  // Use for landing page quote form and account setup
  parcels: {
    /**
     * Lookup a parcel by address, city, and ZIP
     * Public endpoint - no authentication required
     * Returns parcel data including market value, year built, etc.
     * 
     * @param {string} address - Street address
     * @param {string} city - City name
     * @param {string} zip - ZIP code (5-digit)
     * @param {string} [county] - Optional county filter
     */
    lookup: (address, city, zip, county) => {
      const params = new URLSearchParams({ address, city, zip });
      if (county) params.append('county', county);
      return axiosInstance.get(`/parcels/lookup?${params.toString()}`);
    },
    
    /**
     * Search parcels by partial address (for autocomplete)
     * Public endpoint - no authentication required
     * 
     * @param {string} query - Partial address to search
     * @param {Object} [options] - Search options
     * @param {string} [options.city] - City filter
     * @param {string} [options.county] - County filter
     * @param {number} [options.limit=10] - Max results (1-50)
     */
    search: (query, options = {}) => {
      const params = new URLSearchParams({ q: query });
      if (options.city) params.append('city', options.city);
      if (options.county) params.append('county', options.county);
      if (options.limit) params.append('limit', String(options.limit));
      return axiosInstance.get(`/parcels/search?${params.toString()}`);
    },
    
    /**
     * Get full parcel details by property ID
     * Authenticated endpoint - requires login
     * 
     * @param {string} propId - Texas property ID
     */
    getDetails: (propId) => axiosInstance.get(`/parcels/${propId}`),
    
    /**
     * Bulk lookup multiple addresses
     * Authenticated endpoint - requires login
     * 
     * @param {Array<{address: string, city: string, zip: string}>} addresses
     */
    bulkLookup: (addresses) => axiosInstance.post('/parcels/bulk-lookup', { addresses }),
    
    /**
     * Get counties with parcel counts
     * Public endpoint
     */
    getCounties: () => axiosInstance.get('/parcels/counties'),
    
    /**
     * Get parcel database statistics (admin)
     * Authenticated endpoint
     */
    getStats: () => axiosInstance.get('/parcels/stats'),
  },
  
  // Integration methods
  integration: {
    // AI methods
    generateAI: (data) => axiosInstance.post('/integration/ai/generate', data),
    getAIStatus: (jobId) => axiosInstance.get(`/integration/ai/status/${jobId}`),
    
    // Payment methods
    payment: {
      createCheckoutSession: (data) => axiosInstance.post('/integration/payment/create-checkout-session', data),
      createGuestCheckoutSession: (data) => axiosInstance.post('/integration/payment/guest-checkout-session', data),
      getSubscription: () => axiosInstance.get('/integration/payment/subscription'),
      cancelSubscription: () => axiosInstance.post('/integration/payment/cancel-subscription'),
      upgradeTier: (tier) => axiosInstance.post('/integration/payment/upgrade-tier', { tier }),
      createCustomerPortal: (returnUrl) => axiosInstance.post('/integration/payment/customer-portal', { returnUrl }),
      getInvoices: () => axiosInstance.get('/integration/payment/invoices'),
    },
  },

  // Public methods (no authentication required)
  public: {
    // Property analysis for lead generation
    generatePropertyReport: (data) => axiosInstance.post('/public/property-analysis', data),
    getAnalysisStatus: (leadId) => axiosInstance.get(`/public/property-analysis/${leadId}`),
    
    // Health check
    getHealth: () => axiosInstance.get('/public/health'),
  },

  // Maintenance methods (Step 2: Document Issues)
  maintenance: {
    // Report management
    createReport: (propertyId) => axiosInstance.post('/maintenance/reports', { propertyId }),
    getReports: () => axiosInstance.get('/maintenance/reports'),
    getReportByProperty: (propertyId) => axiosInstance.get(`/maintenance/reports/property/${propertyId}`),
    getReport: (reportId) => axiosInstance.get(`/maintenance/reports/${reportId}`),
    updateReportStatus: (reportId, status) => axiosInstance.put(`/maintenance/reports/${reportId}/status`, { status }),
    
    // Finding management
    saveFinding: (data) => axiosInstance.post('/maintenance/findings', data),
    updateFinding: (findingId, data) => axiosInstance.put(`/maintenance/findings/${findingId}`, data),
    deleteFinding: (findingId) => axiosInstance.delete(`/maintenance/findings/${findingId}`),
    
    // Summary for argument generation
    getSummary: (propertyId) => axiosInstance.get(`/maintenance/summary/${propertyId}`),
  },

  // Evidence methods (Photo uploads for maintenance)
  evidence: {
    // Photo/Document Upload flow
    getUploadUrl: (data) => axiosInstance.post('/evidence/upload-url', data),
    confirmUpload: (data) => axiosInstance.post('/evidence/confirm', data),
    // Note: data can include optional checklistItemId for per-item photo categorization
    
    // Photo/Document Evidence management
    getDownloadUrl: (evidenceId) => axiosInstance.get(`/evidence/download/${evidenceId}`),
    getByFinding: (findingId) => axiosInstance.get(`/evidence/finding/${findingId}`),
    getGroupedByItem: (findingId) => axiosInstance.get(`/evidence/finding/${findingId}/grouped`),
    update: (evidenceId, description) => axiosInstance.put(`/evidence/${evidenceId}`, { description }),
    delete: (evidenceId) => axiosInstance.delete(`/evidence/${evidenceId}`),
    
    // Vendor Invoice Upload flow
    getInvoiceUploadUrl: (data) => axiosInstance.post('/evidence/invoice/upload-url', data),
    confirmInvoice: (data) => axiosInstance.post('/evidence/invoice/confirm', data),
    
    // Vendor Invoice management
    getInvoiceDownloadUrl: (invoiceId) => axiosInstance.get(`/evidence/invoice/download/${invoiceId}`),
    getInvoicesByFinding: (findingId) => axiosInstance.get(`/evidence/invoice/finding/${findingId}`),
    deleteInvoice: (invoiceId) => axiosInstance.delete(`/evidence/invoice/${invoiceId}`),
  },

  // Argument generation (Step 4: Talk with Assessor)
  argument: {
    generate: (propertyId) => axiosInstance.post('/integration/ai/generate-argument', { propertyId }, { timeout: 60000 }), // 60s timeout for AI generation
    getByProperty: (propertyId) => axiosInstance.get(`/integration/ai/argument/${propertyId}`),
    getStatus: (jobId) => axiosInstance.get(`/integration/ai/status/${jobId}`),
    getAppealSummary: (propertyId) => axiosInstance.get(`/integration/ai/appeal-summary/${propertyId}`),
    
    // PDF Download methods - return blob for file download
    downloadEvidencePacket: async (propertyId) => {
      const response = await axiosInstance.get(`/integration/ai/evidence-packet/${propertyId}`, {
        responseType: 'blob'
      });
      return response;
    },
    downloadPresentationScript: async (propertyId) => {
      const response = await axiosInstance.get(`/integration/ai/presentation-script/${propertyId}`, {
        responseType: 'blob'
      });
      return response;
    },
  },

  // Flaw findings methods (Step 3: Find Flaws in County's Evidence)
  flaws: {
    // Checklist definitions
    getDefinitions: () => axiosInstance.get('/flaws/definitions'),
    
    // Finding management
    saveFinding: (data) => axiosInstance.post('/flaws/findings', data),
    getPropertyFindings: (propertyId) => axiosInstance.get(`/flaws/findings/property/${propertyId}`),
    getFindingByType: (propertyId, flawType) => axiosInstance.get(`/flaws/findings/property/${propertyId}/type/${flawType}`),
    updateFinding: (findingId, data) => axiosInstance.put(`/flaws/findings/${findingId}`, data),
    deleteFinding: (findingId) => axiosInstance.delete(`/flaws/findings/${findingId}`),
    
    // Summary and progress
    getSummary: (propertyId) => axiosInstance.get(`/flaws/summary/${propertyId}`),
    getProgress: (propertyId) => axiosInstance.get(`/flaws/progress/${propertyId}`),
    
    // Classification guides (presigned S3 URLs for quality/condition PDFs)
    getClassificationGuides: (propertyId) => axiosInstance.get(`/flaws/classification-guides/${propertyId}`),
  },

  // Appeal outcome methods (Post-completion tracking)
  appealOutcome: {
    save: (data) => axiosInstance.post('/appeal-outcome', data),
    getByProperty: (propertyId, year) => axiosInstance.get(`/appeal-outcome/property/${propertyId}${year ? `?year=${year}` : ''}`),
    getUserOutcomes: () => axiosInstance.get('/appeal-outcome/my-outcomes'),
    update: (outcomeId, data) => axiosInstance.put(`/appeal-outcome/${outcomeId}`, data),
    delete: (outcomeId) => axiosInstance.delete(`/appeal-outcome/${outcomeId}`),
  },
};

export default apiClient; 